import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigationType } from "react-router-dom";

// Kept in memory (not sessionStorage): project lists can be too large for the
// storage quota, and only in-app back navigation needs them.
const snapshots = new Map();
const MAX_AGE = 30 * 60 * 1000;
const RESTORE_TIMEOUT = 3000;

// Remembers a page's state and scroll position when it unmounts, and hands
// them back only when the user returns with back/forward (POP). A fresh visit
// (link, menu, reload) starts clean.
//
// const { restored, remember } = usePageSnapshot("key");
// const [x, setX] = useState(restored?.x ?? defaultX);
// remember({ x });  // every render
const usePageSnapshot = (key) => {
  const navigationType = useNavigationType();
  const [restored] = useState(() => {
    const saved = snapshots.get(key);
    if (navigationType !== "POP" || !saved) return null;
    if (Date.now() - saved.savedAt > MAX_AGE) return null;
    return saved;
  });

  const stateRef = useRef(restored?.state ?? null);
  const scrollYRef = useRef(window.scrollY);

  // Track scroll continuously: by the time unmount runs, the next page may
  // already have changed the document height and clamped window.scrollY.
  useEffect(() => {
    const onScroll = () => {
      scrollYRef.current = window.scrollY;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      snapshots.set(key, {
        state: stateRef.current,
        scrollY: scrollYRef.current,
        savedAt: Date.now(),
      });
    };
  }, [key]);

  // Content (images, masonry) grows after mount, so keep trying until the page
  // is tall enough, the target is reached, or the user scrolls themselves.
  useLayoutEffect(() => {
    if (!restored?.scrollY) return;
    const target = restored.scrollY;
    const startedAt = Date.now();
    let frame;
    let cancelled = false;
    const cancel = () => {
      cancelled = true;
    };
    const userEvents = ["wheel", "touchstart", "keydown", "mousedown"];
    userEvents.forEach((e) =>
      window.addEventListener(e, cancel, { passive: true, once: true }),
    );

    const tick = () => {
      if (cancelled) return;
      window.scrollTo(0, target);
      scrollYRef.current = window.scrollY;
      const reached = Math.abs(window.scrollY - target) < 2;
      if (!reached && Date.now() - startedAt < RESTORE_TIMEOUT) {
        frame = requestAnimationFrame(tick);
      }
    };
    tick();

    return () => {
      cancelAnimationFrame(frame);
      userEvents.forEach((e) => window.removeEventListener(e, cancel));
    };
  }, [restored]);

  const remember = (state) => {
    stateRef.current = state;
  };

  return { restored: restored?.state ?? null, remember };
};

export default usePageSnapshot;
